import css from './Avatar.module.css'
import 'photoswipe/dist/photoswipe.css'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { useEffect, useState } from 'react'

interface AvatarParams {
  userID?: string | null
  size: number | null
  photoURL: string | null
  name: string | null
  className?: string | null
  style?: React.CSSProperties | null
  /** Click (or Enter/Space) opens the photo full size, like rewod-mobile */
  zoomable?: boolean
}

// Same viewer settings as rewod-mobile's Avatar
const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" class="pswp__icn" fill="none" stroke="white" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>'

const ZOOM_OPTIONS = {
  closeSVG: CLOSE_ICON,
  showAnimationDuration: 150,
  hideAnimationDuration: 150,
  bgClickAction: 'close' as const,
  clickToCloseNonZoomable: true,
  zoom: false,
  pinchToClose: true,
}

// Profile photos are square; PhotoSwipe needs a size up front
const PHOTO_SIZE = 1024

const Avatar = ({
  userID,
  size,
  photoURL,
  name,
  className,
  style,
  zoomable = false,
}: AvatarParams) => {
  const [localName, setName] = useState<string | null>(name)

  // const getName = async () => {
  //   const userQuery = doc(firestore, 'users', userID)
  //   let newName
  //   try {
  //     const data = await getDoc(userQuery)
  //     setName(data.data().name)
  //     setPhotoURL(data.data().photoURL)
  //   } catch (e) {}
  // }

  useEffect(() => {
    if (userID) setName('NONAME')
  }, [])

  const initials = localName
    ? localName.trim()[0].toUpperCase() !== 'Z'
      ? localName.trim()[0]
      : '/'
    : ''

  const relativeFontSize = size && size * 0.6 < 60 ? size * 0.6 : 60

  const photoStyle: React.CSSProperties = {
    width: size + 'px',
    backgroundImage: `url(${photoURL}`,
    backgroundSize: 'cover',
    ...style,
  }

  if (photoURL && zoomable)
    return (
      <Gallery options={ZOOM_OPTIONS}>
        <Item
          original={photoURL}
          thumbnail={photoURL}
          width={PHOTO_SIZE}
          height={PHOTO_SIZE}
          // The thumbnail is cropped to a circle (object-fit: cover)
          cropped
        >
          {({ ref, open }) => (
            // An <img>, not a background image: PhotoSwipe zooms from the
            // clicked <img>, and falls back to a plain fade without one
            <img
              ref={ref as React.Ref<HTMLImageElement>}
              src={photoURL}
              alt={name ? `View ${name}'s photo` : 'View photo'}
              className={`${css['avatar']} ${css['zoomable']} ${className}`}
              style={{
                width: size + 'px',
                height: size + 'px',
                objectFit: 'cover',
                ...style,
              }}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                open(e)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  // open() needs a mouse event; a click runs the handler above
                  e.currentTarget.click()
                }
              }}
            />
          )}
        </Item>
      </Gallery>
    )

  return photoURL ? (
    <div className={`${css['avatar']} ${className}`} style={photoStyle} />
  ) : (
    <div
      className={`${css['avatar']} ${className}`}
      style={{
        width: size + 'px',
        fontSize: relativeFontSize + 'px',
        ...style,
      }}
    >
      {initials}
    </div>
  )
}

export default Avatar
